import service from './http'
import serve from "@/utils/serve";
import Domain from "@/types/Domain";
import {EnhancedWorkpace} from "@/types/Workspace";

/**
 * Espelha o WorkflowService — o domínio é entidade do ambiente, como o fluxo.
 *
 * O `Partial<Domain>` do patch aceita opção sem id: opção nova vai assim, e quem atribui
 * o id é o servidor, pela sequência. Por isso o tipo do que se envia é mais frouxo que o
 * do que se recebe.
 */
class DomainService {
    async add({systemAlias}: { systemAlias: string }, domain: any) {
        return await serve(async () => await service.post(`systems/${systemAlias}/domains`, domain))
    }

    /**
     * Cria e registra no ambiente em memória, devolvendo o domínio GRAVADO.
     *
     * O que se guarda é o que o servidor devolveu, e não o que se enviou: os ids das
     * opções nascem lá, e a cópia local sem eles não serviria a valor nenhum. Mora aqui
     * porque o domínio é criado de dois lugares — o design do ambiente e o campo de
     * domínio do atributo —, e os dois precisam deixar o workspace no mesmo estado.
     */
    async addTo(workspace: EnhancedWorkpace, domain: Domain): Promise<Domain> {
        const created = await this.add({systemAlias: workspace.alias}, domain)
        workspace.addDomain(created)
        return created
    }

    /**
     * Grava e aplica ao ambiente em memória, espelhando o `addTo` logo acima.
     *
     * O que se guarda é o que o SERVIDOR devolveu: opção nova vai sem id, e é lá que ele
     * nasce — guardar o que se enviou deixaria a opção recém-criada sem valor nenhum para
     * apontar. `Object.assign` no objeto existente, e não troca de referência, para quem já
     * o estava mostrando enxergar a mudança sem reler: o campo de valor default do atributo,
     * o diagrama, a listagem.
     *
     * Mora aqui pelo mesmo motivo do `addTo`: o domínio passou a ser alterado de dois lugares
     * — o design do ambiente e o formulário do atributo —, e os dois precisam deixar o
     * ambiente no mesmo estado. Antes isto vivia só no WorkspaceRouteComponent, e o
     * formulário do atributo não é um componente de rota.
     *
     * No objeto GUARDADO no ambiente, achado pelo alias, e não no que se recebeu: quem pega o domínio
     * por `domainWith` recebe uma cópia (`enhanceDomain`), e era nela que a resposta caía. A opção
     * criada no formulário do atributo era gravada, mas o ambiente continuava com a lista velha, e o
     * formulário reaberto não a mostrava (relato do usuário).
     */
    async updateIn(workspace: EnhancedWorkpace, domain: Domain, changes: Partial<Domain> | any): Promise<Domain> {
        const updated = await this.update({systemAlias: workspace.alias, domainAlias: domain.alias}, changes)
        const stored = workspace.domains.find(candidate => candidate.alias === domain.alias) || domain
        return Object.assign(stored, updated)
    }

    async get({systemAlias, domainAlias}: { systemAlias: string, domainAlias: string }) {
        return await serve(async () => await service.get(`systems/${systemAlias}/domains/${domainAlias}`))
    }

    async update({systemAlias, domainAlias}: { systemAlias: string, domainAlias: string }, patch: Partial<Domain> | any) {
        return await serve(async () => await service.patch(`systems/${systemAlias}/domains/${domainAlias}`, patch))
    }

    async list({systemAlias}: { systemAlias: string }) {
        return await serve(async () => await service.get(`systems/${systemAlias}/domains`))
    }

    async delete({systemAlias, domainAlias}: { systemAlias: string, domainAlias: string }) {
        return await serve(async () => await service.delete(`systems/${systemAlias}/domains/${domainAlias}`))
    }
}

export default DomainService
